import { useState, useCallback, useRef, useMemo } from "react";
import { Bluelagoon } from "@bluelagoon";
import { useEndpointFix } from "./useEndpointFix";
import { extractPathParams, extractQueryParams, extractGraphQLArgs, substitutePath } from "./endpointTestUtils";

const MAX_TESTS = 20;

export function useEndpointTesting({ endpoints, apiType, auth, credentials, addChat, setPhase, baseUrl, integration }) {
    const [currentTestIdx, setCurrentTestIdx] = useState(-1);
    const [currentTestResult, setCurrentTestResult] = useState(null);
    const [currentFix, setCurrentFix] = useState(null);
    const [approvedIds, setApprovedIds] = useState([]);
    const [autoApprove, setAutoApprove] = useState(false);
    const [pendingParams, setPendingParams] = useState(null);
    const autoApproveRef = useRef(autoApprove);
    autoApproveRef.current = autoApprove;

    const readEndpoints = useMemo(() => {
        if (apiType === "graphql") return endpoints.filter((e) => e.method === "QUERY");
        return endpoints.filter((e) => e.method === "GET");
    }, [endpoints, apiType]);

    const testableEndpoints = useMemo(() => readEndpoints.slice(0, MAX_TESTS), [readEndpoints]);
    const currentTestEndpoint = currentTestIdx >= 0 && currentTestIdx < testableEndpoints.length ? testableEndpoints[currentTestIdx] : null;

    const finishTesting = useCallback(() => {
        setPhase("generating_errors");
        addChat({ role: "ai", text: "Testing complete! Reviewing error patterns..." });
        setTimeout(() => { addChat({ role: "ai", text: "Integration is ready to publish!" }); setPhase("complete"); }, 1500);
    }, [addChat, setPhase]);

    const advanceToNext = useCallback((idx, approve) => {
        if (approve) { const ep = testableEndpoints[idx]; if (ep) setApprovedIds((prev) => [...prev, ep.id]); }
        const nextIdx = idx + 1;
        if (nextIdx >= testableEndpoints.length) finishTesting(); else runTest(nextIdx);
    }, [testableEndpoints, finishTesting]);

    const isGql = apiType === "graphql";

    const executeTest = useCallback(async (idx, paramValues) => {
        const ep = testableEndpoints[idx];
        const testPath = isGql ? ep.path : (paramValues ? substitutePath(ep.path, paramValues) : ep.path);
        // Build query params from endpoint schema using testValues (REST only)
        const qps = isGql ? [] : extractQueryParams(ep);
        const queryParams = {};
        for (const qp of qps) { if (qp.testValue) queryParams[qp.name] = qp.testValue; }
        const hasQuery = Object.keys(queryParams).length > 0;
        try {
            const endpointPayload = { method: ep.method, path: testPath };
            if (isGql) { endpointPayload.query = ep.query || ""; endpointPayload.variables = paramValues || {}; }
            const res = await Bluelagoon.testIntegrationEndpoint({ baseUrl: baseUrl || "", endpoint: endpointPayload, auth, credentials, ...(hasQuery ? { queryParams } : {}) });
            const result = res?.data?.data || res?.data || { status: 0, ms: 0, body: { error: "No response" } };
            setCurrentTestResult(result);
            const ok = result.status < 400;
            addChat({ role: "ai", text: ok ? `**${ep.method} ${testPath}** — ${result.status} in ${result.ms}ms` : `**${ep.method} ${testPath}** returned ${result.status}` });
            if (autoApproveRef.current) setTimeout(() => advanceToNext(idx, ok), ok ? 1200 : 1500);
        } catch (err) {
            setCurrentTestResult({ status: 0, ms: 0, body: { error: err.message } });
            addChat({ role: "ai", text: `Failed to test **${testPath}**: ${err.message}` });
            if (autoApproveRef.current) setTimeout(() => advanceToNext(idx, false), 1500);
        }
    }, [testableEndpoints, auth, credentials, addChat, baseUrl, advanceToNext, isGql]);

    const runTest = useCallback(async (idx) => {
        if (idx >= testableEndpoints.length) { finishTesting(); return; }
        const ep = testableEndpoints[idx];
        setCurrentTestIdx(idx); setCurrentTestResult(null); setCurrentFix(null); setPendingParams(null);

        // GraphQL: use arguments instead of path params
        if (isGql) {
            const gqlArgs = extractGraphQLArgs(ep);
            const requiredArgs = gqlArgs.filter((a) => a.required);
            const needsUser = requiredArgs.some((a) => a.needsUser);
            if (requiredArgs.length > 0 && needsUser) {
                const defaults = {}; requiredArgs.forEach((a) => { defaults[a.name] = a.testValue === "__ASK_USER__" ? "" : a.testValue; });
                setPendingParams({ params: requiredArgs, values: defaults });
                addChat({ role: "ai", text: `**${ep.method} ${ep.path}** needs variable values to test.` });
                return;
            }
            const v = {}; gqlArgs.forEach((a) => { if (a.testValue && a.testValue !== "__ASK_USER__") v[a.name] = a.testValue; });
            await executeTest(idx, Object.keys(v).length > 0 ? v : null);
            return;
        }

        const pathParams = extractPathParams(ep);
        const needsUser = pathParams.some((p) => p.needsUser);
        if (pathParams.length > 0 && needsUser) {
            const defaults = {}; pathParams.forEach((p) => { defaults[p.name] = p.testValue === "__ASK_USER__" ? "" : p.testValue; });
            setPendingParams({ params: pathParams, values: defaults });
            addChat({ role: "ai", text: `**${ep.method} ${ep.path}** needs parameter values to test.` });
            return;
        }
        if (pathParams.length > 0) { const v = {}; pathParams.forEach((p) => { v[p.name] = p.testValue; }); await executeTest(idx, v); }
        else await executeTest(idx, null);
    }, [testableEndpoints, finishTesting, addChat, executeTest, isGql]);

    const submitParams = useCallback((values) => { setPendingParams(null); executeTest(currentTestIdx, values); }, [currentTestIdx, executeTest]);

    const retestEndpoint = useCallback((newPath) => {
        setCurrentTestResult(null); setCurrentFix(null);
        const ep = testableEndpoints[currentTestIdx]; if (!ep) return;
        if (newPath && newPath !== ep.path) { ep.path = newPath; addChat({ role: "ai", text: `Retesting: **${ep.method} ${newPath}**` }); }
        const pp = extractPathParams(ep);
        if (pp.length > 0) { const v = {}; pp.forEach((p) => { v[p.name] = p.testValue || ""; }); if (pp.some((p) => p.needsUser)) { setPendingParams({ params: pp, values: v }); return; } executeTest(currentTestIdx, v); }
        else executeTest(currentTestIdx, null);
    }, [currentTestIdx, testableEndpoints, executeTest, addChat]);

    const onFixApplied = useCallback((fixedIntegration) => {
        if (!fixedIntegration?.endpoints) return;
        const ep = testableEndpoints[currentTestIdx]; if (!ep) return;
        const fixed = fixedIntegration.endpoints.find((e) => e.resource === ep.resource && e.method === ep.method);
        if (fixed) { ep.path = fixed.path; ep.parameters = fixed.parameters; ep.description = fixed.description || ep.description; addChat({ role: "ai", text: `Fixed: **${ep.method} ${fixed.path}**. Re-testing...` }); }
        retestEndpoint(ep.path);
    }, [testableEndpoints, currentTestIdx, addChat, retestEndpoint]);

    const endpointFix = useEndpointFix({ addChat, onFixApplied });

    const fixWithAI = useCallback(() => {
        const ep = testableEndpoints[currentTestIdx]; if (!ep) return;
        endpointFix.requestFix({ endpoint: ep, error: currentTestResult, integration: { ...integration, endpoints, auth }, credentials });
    }, [currentTestIdx, testableEndpoints, currentTestResult, integration, endpoints, auth, credentials, endpointFix]);

    const startTesting = useCallback(() => {
        if (readEndpoints.length > MAX_TESTS) { addChat({ role: "ai", text: `Found **${readEndpoints.length}** endpoints. Testing the first **${MAX_TESTS}**.` }); }
        addChat({ role: "ai", text: "Starting endpoint tests..." }); setPhase("testing"); setCurrentTestIdx(0); runTest(0);
    }, [addChat, runTest, readEndpoints, setPhase]);

    return {
        currentTestEndpoint, currentTestResult, currentFix, pendingParams, approvedIds, autoApprove,
        startTesting, submitParams, approveEndpoint: useCallback(() => advanceToNext(currentTestIdx, true), [currentTestIdx, advanceToNext]),
        skipEndpoint: useCallback(() => advanceToNext(currentTestIdx, false), [currentTestIdx, advanceToNext]),
        toggleAutoApprove: useCallback(() => setAutoApprove((p) => !p), []),
        retestEndpoint, fixWithAI, isFixing: endpointFix.isFixing,
    };
}
